/**
 * SMEInsightPanel - Enhanced AI Insight Display for Subject Matter Experts
 * Clear, readable text-based AI instructions with semantic highlighting
 * Designed for desktop viewing with comprehensive detail levels
 */

import { useState, useMemo } from 'react'
import clsx from 'clsx'
import {
  Brain,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Lightbulb,
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  Target,
  Activity,
  Clock,
  Sparkles,
  Zap,
  FileText,
  ArrowRight,
  BarChart3,
  History
} from 'lucide-react'

// ============================================================================
// TYPES
// ============================================================================

type InsightType = 'analysis' | 'prediction' | 'recommendation' | 'alert' | 'summary'
type SeverityLevel = 'critical' | 'high' | 'medium' | 'low' | 'info'

interface KeyMetric {
  label: string
  value: string | number
  unit?: string
  trend?: 'up' | 'down' | 'stable'
  status?: 'critical' | 'warning' | 'good' | 'neutral'
  target?: string | number
  description?: string
}

interface Finding {
  title: string
  description: string
  severity: SeverityLevel
  evidence?: string
  impact?: string
}

interface Recommendation {
  id: string
  priority: 'immediate' | 'high' | 'medium' | 'low'
  action: string
  rationale: string
  expectedOutcome?: string
  timeframe?: string
}

interface SMEInsightPanelProps {
  title: string
  headline: string
  summary: string
  type?: InsightType
  severity?: SeverityLevel
  confidence?: number
  keyMetrics?: KeyMetric[]
  findings?: Finding[]
  recommendations?: Recommendation[]
  technicalDetails?: string
  methodology?: string
  dataQuality?: {
    completeness: number
    freshness: string
    sources: string[]
  }
  historicalContext?: string
  modelInfo?: {
    name: string
    version: string
    lastUpdated: string
  }
  timestamp?: string
  className?: string
}

// ============================================================================
// CONFIGURATION
// ============================================================================

const TYPE_CONFIG = {
  analysis: {
    icon: BarChart3,
    label: 'Analysis',
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30'
  },
  prediction: {
    icon: TrendingUp,
    label: 'Prediction',
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30'
  },
  recommendation: {
    icon: Lightbulb,
    label: 'Recommendation',
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/30'
  },
  alert: {
    icon: AlertTriangle,
    label: 'Alert',
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/30'
  },
  summary: {
    icon: FileText,
    label: 'Summary',
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/10',
    borderColor: 'border-cyan-500/30'
  }
}

const SEVERITY_CONFIG = {
  critical: { label: 'CRITICAL', color: 'text-red-400', bgColor: 'bg-red-500', pulse: true },
  high: { label: 'HIGH', color: 'text-amber-400', bgColor: 'bg-amber-500', pulse: true },
  medium: { label: 'MEDIUM', color: 'text-yellow-400', bgColor: 'bg-yellow-500', pulse: false },
  low: { label: 'LOW', color: 'text-cyan-400', bgColor: 'bg-cyan-500', pulse: false },
  info: { label: 'INFO', color: 'text-surface-400', bgColor: 'bg-surface-500', pulse: false }
}

const PRIORITY_CONFIG = {
  immediate: { label: 'IMMEDIATE', color: 'text-red-400', bgColor: 'bg-red-500/20', borderColor: 'border-red-500/30' },
  high: { label: 'HIGH', color: 'text-amber-400', bgColor: 'bg-amber-500/20', borderColor: 'border-amber-500/30' },
  medium: { label: 'MEDIUM', color: 'text-cyan-400', bgColor: 'bg-cyan-500/20', borderColor: 'border-cyan-500/30' },
  low: { label: 'LOW', color: 'text-surface-400', bgColor: 'bg-surface-700', borderColor: 'border-surface-600' }
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

/** Semantic text formatter - highlights numbers, percentages, and key terms */
function FormattedText({ text }: { text: string }) {
  const parts = useMemo(() => {
    // Regex to match various patterns
    const pattern = /(\d+\.?\d*%|\$[\d,]+(?:\.\d{2})?[KMB]?|\d+(?:,\d{3})*(?:\.\d+)?(?:\s*(?:hours?|hrs?|days?|weeks?|months?|PSI|°F|°C|RPM|MMSCFD))?|\b(?:critical|warning|normal|healthy|failed|urgent|immediate)\b)/gi

    return text.split(pattern).map((part, i) => {
      // Numbers and percentages
      if (/^\d+\.?\d*%$/.test(part)) {
        return <span key={i} className="font-mono font-semibold text-primary-400">{part}</span>
      }
      // Currency
      if (/^\$/.test(part)) {
        return <span key={i} className="font-mono font-semibold text-emerald-400">{part}</span>
      }
      // Numbers with units
      if (/^\d+(?:,\d{3})*(?:\.\d+)?/.test(part)) {
        return <span key={i} className="font-mono font-semibold text-blue-400">{part}</span>
      }
      // Status words
      if (/^(critical|failed|urgent)$/i.test(part)) {
        return <span key={i} className="font-semibold text-red-400">{part}</span>
      }
      if (/^(warning|immediate)$/i.test(part)) {
        return <span key={i} className="font-semibold text-amber-400">{part}</span>
      }
      if (/^(normal|healthy)$/i.test(part)) {
        return <span key={i} className="font-semibold text-emerald-400">{part}</span>
      }
      return part
    })
  }, [text])

  return <>{parts}</>
}

/** Key metric card with visual indicator */
function MetricCard({ metric }: { metric: KeyMetric }) {
  const statusColors = {
    critical: 'border-red-500/40 bg-red-500/10',
    warning: 'border-amber-500/40 bg-amber-500/10',
    good: 'border-emerald-500/40 bg-emerald-500/10',
    neutral: 'border-surface-600 bg-surface-800/50'
  }

  const textColors = {
    critical: 'text-red-400',
    warning: 'text-amber-400',
    good: 'text-emerald-400',
    neutral: 'text-surface-200'
  }

  const TrendIcon = metric.trend === 'up' ? TrendingUp : metric.trend === 'down' ? TrendingDown : Minus

  return (
    <div className={clsx(
      'p-4 rounded-lg border',
      statusColors[metric.status || 'neutral']
    )}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-surface-400 uppercase tracking-wider font-medium">
            {metric.label}
          </p>
          <div className="flex items-baseline gap-2 mt-1">
            <span className={clsx('text-2xl font-mono font-bold', textColors[metric.status || 'neutral'])}>
              {metric.value}
            </span>
            {metric.unit && (
              <span className="text-sm text-surface-500">{metric.unit}</span>
            )}
          </div>
        </div>
        {metric.trend && (
          <TrendIcon size={20} className={clsx(
            metric.trend === 'up' ? 'text-emerald-400' :
            metric.trend === 'down' ? 'text-red-400' : 'text-surface-500'
          )} />
        )}
      </div>
      {metric.target && (
        <div className="mt-2 pt-2 border-t border-surface-700/50">
          <div className="flex items-center gap-2">
            <Target size={12} className="text-surface-500" />
            <span className="text-xs text-surface-500">
              Target: <span className="text-surface-300 font-mono">{metric.target}{metric.unit}</span>
            </span>
          </div>
        </div>
      )}
      {metric.description && (
        <p className="text-xs text-surface-500 mt-2">{metric.description}</p>
      )}
    </div>
  )
}

/** Finding card with severity indicator */
function FindingCard({ finding }: { finding: Finding }) {
  const config = SEVERITY_CONFIG[finding.severity]
  const Icon = finding.severity === 'critical' || finding.severity === 'high' ? AlertTriangle :
               finding.severity === 'medium' ? AlertCircle : Activity

  return (
    <div className={clsx(
      'p-4 rounded-lg border',
      finding.severity === 'critical' ? 'bg-red-500/5 border-red-500/30' :
      finding.severity === 'high' ? 'bg-amber-500/5 border-amber-500/30' :
      finding.severity === 'medium' ? 'bg-yellow-500/5 border-yellow-500/30' :
      'bg-surface-800/50 border-surface-700'
    )}>
      <div className="flex items-start gap-3">
        <div className={clsx('p-2 rounded-lg', config.bgColor + '/20')}>
          <Icon size={18} className={config.color} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-semibold text-surface-100">{finding.title}</h4>
            <span className={clsx(
              'text-[10px] font-bold uppercase px-2 py-0.5 rounded',
              config.bgColor + '/20', config.color
            )}>
              {config.label}
            </span>
          </div>
          <p className="text-sm text-surface-300 leading-relaxed">
            <FormattedText text={finding.description} />
          </p>
          {finding.evidence && (
            <div className="mt-3 p-2 bg-surface-900/50 rounded border border-surface-700/50">
              <p className="text-xs text-surface-400">
                <span className="font-semibold text-surface-300">Evidence:</span> {finding.evidence}
              </p>
            </div>
          )}
          {finding.impact && (
            <div className="mt-2 flex items-start gap-2">
              <Zap size={12} className="text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-surface-400">
                <span className="font-semibold">Impact:</span> {finding.impact}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/** Recommendation card with actionable steps */
function RecommendationCard({ recommendation }: { recommendation: Recommendation }) {
  const config = PRIORITY_CONFIG[recommendation.priority]

  return (
    <div className={clsx(
      'p-4 rounded-lg border',
      config.bgColor,
      config.borderColor
    )}>
      <div className="flex items-start gap-3">
        <div className={clsx(
          'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
          config.bgColor,
          'border',
          config.borderColor
        )}>
          <ArrowRight size={16} className={config.color} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className={clsx('text-[10px] font-bold uppercase px-2 py-0.5 rounded border', config.bgColor, config.borderColor, config.color)}>
              {config.label}
            </span>
            {recommendation.timeframe && (
              <span className="text-xs text-surface-500 flex items-center gap-1">
                <Clock size={10} />
                {recommendation.timeframe}
              </span>
            )}
          </div>
          <h4 className="font-semibold text-surface-100 text-base mb-2">
            {recommendation.action}
          </h4>
          <p className="text-sm text-surface-300 leading-relaxed">
            <FormattedText text={recommendation.rationale} />
          </p>
          {recommendation.expectedOutcome && (
            <div className="mt-3 p-3 bg-emerald-500/5 rounded-lg border border-emerald-500/20">
              <div className="flex items-start gap-2">
                <CheckCircle size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-emerald-400 mb-1">Expected Outcome</p>
                  <p className="text-sm text-surface-300">{recommendation.expectedOutcome}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function SMEInsightPanel({
  title,
  headline,
  summary,
  type = 'analysis',
  severity = 'info',
  confidence,
  keyMetrics = [],
  findings = [],
  recommendations = [],
  technicalDetails,
  methodology,
  dataQuality,
  historicalContext,
  modelInfo,
  timestamp,
  className
}: SMEInsightPanelProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['metrics', 'findings', 'recommendations']))
  const [copied, setCopied] = useState(false)

  const typeConfig = TYPE_CONFIG[type]
  const severityConfig = SEVERITY_CONFIG[severity]
  const TypeIcon = typeConfig.icon

  const toggleSection = (section: string) => {
    const newSections = new Set(expandedSections)
    if (newSections.has(section)) {
      newSections.delete(section)
    } else {
      newSections.add(section)
    }
    setExpandedSections(newSections)
  }

  const handleCopy = async () => {
    const text = `${title}\n\n${headline}\n\n${summary}\n\nFindings:\n${findings.map(f => `- ${f.title}: ${f.description}`).join('\n')}\n\nRecommendations:\n${recommendations.map(r => `- [${r.priority.toUpperCase()}] ${r.action}`).join('\n')}`
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className={clsx(
      'rounded-xl border-2 overflow-hidden bg-surface-900',
      severityConfig.pulse ? 'border-red-500/40' : typeConfig.borderColor,
      className
    )}>
      {/* Header */}
      <div className={clsx('p-6', typeConfig.bgColor)}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className={clsx('p-3 rounded-xl', typeConfig.bgColor, 'border', typeConfig.borderColor)}>
              <TypeIcon size={28} className={typeConfig.color} />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className={clsx('text-xs font-bold uppercase tracking-wider', typeConfig.color)}>
                  {typeConfig.label}
                </span>
                {severity !== 'info' && (
                  <span className={clsx(
                    'text-[10px] font-bold uppercase px-2 py-0.5 rounded',
                    severityConfig.bgColor + '/20',
                    severityConfig.color,
                    severityConfig.pulse && 'animate-pulse'
                  )}>
                    {severityConfig.label}
                  </span>
                )}
              </div>
              <h2 className="text-xl font-bold text-white">{title}</h2>
              {modelInfo && (
                <p className="text-xs text-surface-400 mt-1">
                  {modelInfo.name} v{modelInfo.version} - Updated {modelInfo.lastUpdated}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Confidence Indicator */}
            {confidence !== undefined && (
              <div className="text-center px-4 py-3 bg-surface-800/50 rounded-xl border border-surface-700">
                <div className="flex items-center gap-1 mb-1">
                  <Sparkles size={12} className="text-primary-400" />
                  <span className="text-[10px] text-surface-500 uppercase tracking-wider">AI Confidence</span>
                </div>
                <span className={clsx(
                  'text-2xl font-bold font-mono',
                  confidence >= 0.85 ? 'text-emerald-400' :
                  confidence >= 0.70 ? 'text-amber-400' : 'text-red-400'
                )}>
                  {Math.round(confidence * 100)}%
                </span>
              </div>
            )}

            <button
              onClick={handleCopy}
              className={clsx(
                'p-3 rounded-xl border transition-colors',
                copied
                  ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400'
                  : 'bg-surface-800/50 border-surface-700 text-surface-400 hover:text-surface-200'
              )}
            >
              {copied ? <Check size={18} /> : <Copy size={18} />}
            </button>
          </div>
        </div>

        {/* Headline & Summary */}
        <div className="mt-6 space-y-4">
          <h3 className="text-lg font-semibold text-surface-100 leading-snug">
            <FormattedText text={headline} />
          </h3>
          <p className="text-base text-surface-300 leading-relaxed">
            <FormattedText text={summary} />
          </p>
        </div>
      </div>

      {/* Key Metrics Section */}
      {keyMetrics.length > 0 && (
        <div className="border-t border-surface-700/50">
          <button
            onClick={() => toggleSection('metrics')}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-surface-800/30 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Activity size={18} className="text-primary-400" />
              <span className="font-semibold text-surface-200">Key Metrics</span>
              <span className="text-xs text-surface-500 bg-surface-700 px-2 py-0.5 rounded">
                {keyMetrics.length}
              </span>
            </div>
            {expandedSections.has('metrics') ? <ChevronUp size={18} className="text-surface-400" /> : <ChevronDown size={18} className="text-surface-400" />}
          </button>
          {expandedSections.has('metrics') && (
            <div className="px-6 pb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              {keyMetrics.map((metric, i) => <MetricCard key={i} metric={metric} />)}
            </div>
          )}
        </div>
      )}

      {/* Findings Section */}
      {findings.length > 0 && (
        <div className="border-t border-surface-700/50">
          <button
            onClick={() => toggleSection('findings')}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-surface-800/30 transition-colors"
          >
            <div className="flex items-center gap-3">
              <AlertCircle size={18} className="text-amber-400" />
              <span className="font-semibold text-surface-200">Findings</span>
              <span className="text-xs text-surface-500 bg-surface-700 px-2 py-0.5 rounded">
                {findings.length}
              </span>
            </div>
            {expandedSections.has('findings') ? <ChevronUp size={18} className="text-surface-400" /> : <ChevronDown size={18} className="text-surface-400" />}
          </button>
          {expandedSections.has('findings') && (
            <div className="px-6 pb-6 space-y-4">
              {findings.map((finding, i) => <FindingCard key={i} finding={finding} />)}
            </div>
          )}
        </div>
      )}

      {/* Recommendations Section */}
      {recommendations.length > 0 && (
        <div className="border-t border-surface-700/50">
          <button
            onClick={() => toggleSection('recommendations')}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-surface-800/30 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Lightbulb size={18} className="text-emerald-400" />
              <span className="font-semibold text-surface-200">Recommendations</span>
              <span className="text-xs text-surface-500 bg-surface-700 px-2 py-0.5 rounded">
                {recommendations.length}
              </span>
            </div>
            {expandedSections.has('recommendations') ? <ChevronUp size={18} className="text-surface-400" /> : <ChevronDown size={18} className="text-surface-400" />}
          </button>
          {expandedSections.has('recommendations') && (
            <div className="px-6 pb-6 space-y-4">
              {recommendations.map((rec, i) => <RecommendationCard key={i} recommendation={rec} />)}
            </div>
          )}
        </div>
      )}

      {/* Technical Details Section */}
      {(technicalDetails || methodology || historicalContext) && (
        <div className="border-t border-surface-700/50">
          <button
            onClick={() => toggleSection('technical')}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-surface-800/30 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Brain size={18} className="text-purple-400" />
              <span className="font-semibold text-surface-200">Technical Details</span>
            </div>
            {expandedSections.has('technical') ? <ChevronUp size={18} className="text-surface-400" /> : <ChevronDown size={18} className="text-surface-400" />}
          </button>
          {expandedSections.has('technical') && (
            <div className="px-6 pb-6 space-y-4">
              {technicalDetails && (
                <div className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
                  <h5 className="text-xs font-semibold text-surface-400 uppercase tracking-wider mb-2">Technical Analysis</h5>
                  <p className="text-sm text-surface-300 leading-relaxed">
                    <FormattedText text={technicalDetails} />
                  </p>
                </div>
              )}
              {methodology && (
                <div className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
                  <h5 className="text-xs font-semibold text-surface-400 uppercase tracking-wider mb-2">Methodology</h5>
                  <p className="text-sm text-surface-300 leading-relaxed">{methodology}</p>
                </div>
              )}
              {historicalContext && (
                <div className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
                  <div className="flex items-center gap-2 mb-2">
                    <History size={14} className="text-surface-400" />
                    <h5 className="text-xs font-semibold text-surface-400 uppercase tracking-wider">Historical Context</h5>
                  </div>
                  <p className="text-sm text-surface-300 leading-relaxed">{historicalContext}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Data Quality Section */}
      {dataQuality && (
        <div className="border-t border-surface-700/50">
          <button
            onClick={() => toggleSection('quality')}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-surface-800/30 transition-colors"
          >
            <div className="flex items-center gap-3">
              <CheckCircle size={18} className="text-cyan-400" />
              <span className="font-semibold text-surface-200">Data Quality</span>
            </div>
            {expandedSections.has('quality') ? <ChevronUp size={18} className="text-surface-400" /> : <ChevronDown size={18} className="text-surface-400" />}
          </button>
          {expandedSections.has('quality') && (
            <div className="px-6 pb-6">
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-surface-800/50 rounded-lg border border-surface-700 text-center">
                  <p className="text-xs text-surface-500 uppercase tracking-wider mb-1">Completeness</p>
                  <p className={clsx(
                    'text-xl font-mono font-bold',
                    dataQuality.completeness >= 90 ? 'text-emerald-400' :
                    dataQuality.completeness >= 70 ? 'text-amber-400' : 'text-red-400'
                  )}>
                    {dataQuality.completeness}%
                  </p>
                </div>
                <div className="p-3 bg-surface-800/50 rounded-lg border border-surface-700 text-center">
                  <p className="text-xs text-surface-500 uppercase tracking-wider mb-1">Freshness</p>
                  <p className="text-base font-medium text-surface-200">{dataQuality.freshness}</p>
                </div>
                <div className="p-3 bg-surface-800/50 rounded-lg border border-surface-700 text-center">
                  <p className="text-xs text-surface-500 uppercase tracking-wider mb-1">Sources</p>
                  <p className="text-base font-medium text-surface-200">{dataQuality.sources.length}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="px-6 py-4 bg-surface-800/30 border-t border-surface-700/50 flex items-center justify-between">
        <div className="flex items-center gap-4 text-xs text-surface-500">
          <span className="flex items-center gap-1">
            <Brain size={12} />
            AI-Generated Analysis
          </span>
          {timestamp && (
            <span className="flex items-center gap-1">
              <Clock size={12} />
              {timestamp}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
